import { useState, useCallback, useEffect } from 'react';
import { ordersApi } from '../api';

const DEFAULT_PAGE_SIZE = 20;

function formatError(err) {
  if (err?.data?.message) return err.data.message;
  if (err?.message) return err.message;
  return 'Loi khong xac dinh';
}

export default function useOrders() {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: DEFAULT_PAGE_SIZE, totalElements: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ status: null });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadOrders = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        provider: 'FMARKET',
        status: overrides.status !== undefined ? overrides.status : filters.status,
        fromDate: overrides.fromDate || filters.fromDate,
        toDate: overrides.toDate || filters.toDate,
        page: overrides.page !== undefined ? overrides.page : pagination.page,
        size: DEFAULT_PAGE_SIZE,
      };
      const res = await ordersApi.getOrderHistory(params);
      const data = res.data || {};
      setOrders(data.items || []);
      setPagination({
        page: data.page ?? 0,
        size: data.size ?? DEFAULT_PAGE_SIZE,
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
      });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const changePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const selectOrder = useCallback(async (orderId) => {
    setDetailLoading(true);
    try {
      const res = await ordersApi.getOrderDetail(orderId);
      setSelectedOrder(res.data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const approveOrder = useCallback(async (orderId) => {
    setDetailLoading(true);
    setError(null);
    try {
      await ordersApi.approveOrder(orderId);
      setSelectedOrder(null);
      loadOrders({ status: filters.status, page: pagination.page });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setDetailLoading(false);
    }
  }, [filters.status, pagination.page, loadOrders]);

  const confirmPayment = useCallback(async (orderId) => {
    setDetailLoading(true);
    setError(null);
    try {
      await ordersApi.confirmPayment('FMARKET', orderId);
      setSelectedOrder(null);
      loadOrders({ status: filters.status, page: pagination.page });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setDetailLoading(false);
    }
  }, [filters.status, pagination.page, loadOrders]);

  const openWebview = useCallback(async () => {
    try {
      const res = await ordersApi.getWebviewAccess('FMARKET');
      if (res.data?.webviewUrl) {
        window.open(res.data.webviewUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(formatError(err));
    }
  }, []);

  const syncOrderStatus = useCallback(async (orderId) => {
    try {
      const res = await ordersApi.syncOrderStatus(orderId);
      if (res.data?.updated) {
        loadOrders({ status: filters.status, page: pagination.page });
      }
      return res.data;
    } catch (err) {
      setError(formatError(err));
      return null;
    }
  }, [filters.status, pagination.page, loadOrders]);

  const clearSelection = useCallback(() => {
    setSelectedOrder(null);
  }, []);

  useEffect(() => {
    loadOrders({ status: filters.status, page: pagination.page });
  }, [filters.status, pagination.page]);

  return {
    orders,
    pagination,
    filters,
    selectedOrder,
    loading,
    detailLoading,
    error,
    loadOrders,
    updateFilters,
    changePage,
    selectOrder,
    clearSelection,
    approveOrder,
    confirmPayment,
    openWebview,
    syncOrderStatus,
  };
}
